/**
 * AppealCreate Page
 * 
 * 4-step wizard for creating AI-powered appeal letters:
 * Step 1: Template/procedure type selection + payer address
 * Step 2: Review claim context + supplement clinical data via structured forms
 * Step 3: Agent chat for validation, missing data collection, and generation
 * Step 4: Letter editor with View Sources panel + Attachment Checklist
 */

import React, { useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  CardContent, 
  Typography, 
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Tooltip,
} from '@mui/material';
import { 
  AutoAwesome as AIIcon,
  ArrowBack as BackIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { 
  PolicySearchPanel, 
  ClaimContextPanel,
  AgentChat,
  ClinicalSupplementsForm,
  AttachmentChecklist,
  getDefaultClinicalSupplements,
  getDefaultAttachments,
  getProcedureCategoryLabel,
  isPostOpCourseNeeded,
} from '../components';
import { 
  detectPayerTemplate,
  PAYER_TEMPLATES,
} from '../constants/appealTemplates';
import {
  useGenerateAppealLetterMutation,
  TemplateType,
  AppealType,
  GenerateAppealLetterRequest,
  ProcedureCategory,
  DenialCategory,
  PayerTemplateId,
} from '../../../services/AppealsService';
import type { ClinicalSupplements } from '../components/forms/ClinicalSupplementsForm';
import type { AttachmentItem } from '../components/forms/AttachmentChecklist';

const TEMPLATE_TYPES: { value: TemplateType; label: string; description: string }[] = [
  { value: 'MedicalNecessity', label: 'Medical Necessity', description: 'Appeal based on medical necessity for the procedure or service' },
  { value: 'PriorAuthDenial', label: 'Prior Authorization Denial', description: 'Appeal a denial due to lack of prior authorization' },
  { value: 'CodingDispute', label: 'Coding Dispute', description: 'Appeal incorrect coding or bundling issues' },
  { value: 'TimelyCoverage', label: 'Timely Filing/Coverage', description: 'Appeal timely filing or coverage determination issues' },
  { value: 'BillingError', label: 'Billing Error', description: 'Appeal claim denied due to billing errors' },
  { value: 'OutOfNetwork', label: 'Out of Network', description: 'Appeal out-of-network denial' },
  { value: 'FreeForm', label: 'Free Form', description: 'Custom appeal letter without a specific template' },
];

const APPEAL_TYPES: { value: AppealType; label: string }[] = [
  { value: 'FirstLevel', label: 'First Level Appeal' },
  { value: 'SecondLevel', label: 'Second Level Appeal' },
  { value: 'ExternalReview', label: 'External Review' },
  { value: 'Reconsideration', label: 'Reconsideration' },
  { value: 'PeerToPeer', label: 'Peer-to-Peer Review' },
];

const PROCEDURE_CATEGORIES: { value: ProcedureCategory; label: string }[] = [
  { value: 'Spine', label: 'Spine Surgery' },
  { value: 'Nerve', label: 'Nerve / Carpal / Cubital Tunnel' },
  { value: 'Arthroscopy', label: 'Arthroscopy' },
  { value: 'General', label: 'General Procedure' },
];

const steps = ['Configure Appeal', 'Clinical Documentation', 'AI Generation', 'Review & Finalize'];

const AppealCreate: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get params from URL
  const encounterId = searchParams.get('encounterId') || '';
  const accountId = searchParams.get('accountId') || '';
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const remitId = searchParams.get('remitId') || '';
  const claimIndexParam = searchParams.get('claimIndex');
  const claimIndex = claimIndexParam ? parseInt(claimIndexParam, 10) : undefined;

  // Step 1 state
  const [activeStep, setActiveStep] = useState(0);
  const [templateType, setTemplateType] = useState<TemplateType>('MedicalNecessity');
  const [appealType, setAppealType] = useState<AppealType>('FirstLevel');
  const [procedureCategory, setProcedureCategory] = useState<ProcedureCategory>('General');
  const [denialCategory] = useState<DenialCategory>('Clinical');
  const [payerTemplateId, setPayerTemplateId] = useState<PayerTemplateId>('GENERIC');
  const [payerAddress, setPayerAddress] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [includePolicySearch, setIncludePolicySearch] = useState(true);
  const [searchTermOverrides, setSearchTermOverrides] = useState<string[]>([]);
  
  // Step 2 state - clinical supplements
  const [clinicalSupplements, setClinicalSupplements] = useState<ClinicalSupplements>(
    getDefaultClinicalSupplements()
  );
  
  // Step 4 state - attachments
  const [attachments, setAttachments] = useState<AttachmentItem[]>(() => 
    getDefaultAttachments(procedureCategory, denialCategory)
  );
  
  // Generation state
  const [generateAppeal, { isLoading, error, data: generatedAppeal }] = useGenerateAppealLetterMutation();
  const [generatedLetterId, setGeneratedLetterId] = useState<string | null>(null);

  // Determine if post-op course is needed
  const showPostOpCourse = isPostOpCourseNeeded(denialCategory, payerTemplateId);
  
  // Get selected payer template info
  const selectedPayerTemplate = PAYER_TEMPLATES.find(t => t.id === payerTemplateId);

  const handleNext = () => {
    if (activeStep === 2) {
      // Generate the appeal letter
      handleGenerateAppeal();
    } else if (activeStep === steps.length - 1) {
      // Final step - go to editor
      if (generatedLetterId) {
        navigate(`/appeals/edit/${generatedLetterId}?company=${company}&center=${center}`);
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleGenerateAppeal = async () => {
    const request: GenerateAppealLetterRequest = {
      encounterId,
      accountId,
      templateType,
      appealType,
      additionalInstructions: additionalInstructions || undefined,
      includePolicySearch,
      searchTermOverrides: searchTermOverrides.length > 0 ? searchTermOverrides : undefined,
      
      // Enhanced fields
      procedureCategory,
      denialCategory,
      payerTemplateId,
      payerAddress,

      // Clinical supplements
      clinicalSupplements: {
        painSeverity: clinicalSupplements.painSeverity,
        painDescription: clinicalSupplements.painDescription,
        symptomOnset: clinicalSupplements.symptomOnset,
        symptomDuration: clinicalSupplements.symptomDuration,
        functionalImpact: clinicalSupplements.functionalImpact,
        functionalImpactNotes: clinicalSupplements.functionalImpactNotes,
        examFindings: clinicalSupplements.examFindings,
        examFindingsNotes: clinicalSupplements.examFindingsNotes,
        imagingResults: clinicalSupplements.imagingResults,
        failedTreatments: clinicalSupplements.failedTreatments,
        intraopFindings: clinicalSupplements.intraopFindings,
        postOpCourse: clinicalSupplements.postOpCourse,
        additionalNotes: clinicalSupplements.additionalNotes,
      },
    };

    try {
      const result = await generateAppeal(request).unwrap();
      setGeneratedLetterId(result.id);
      setActiveStep(3); // Move to review step
    } catch (err) {
      console.error('Failed to generate appeal:', err);
    }
  };

  const handleBackToWorklist = () => {
    navigate(`/appeals?encounterId=${encounterId}&company=${company}&center=${center}`);
  };

  const handleAppealGenerated = (content: string) => {
    // Callback when agent generates content - could update local state
    console.log('Agent generated content:', content.substring(0, 100) + '...');
  };

  if (!encounterId) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard sx={{ maxWidth: 800 }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="error">
                Missing encounter information. Please access this page from the Appeals worklist.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate('/appeals')}
                sx={{ mt: 2 }}
              >
                Go to Appeals Worklist
              </Button>
            </CardContent>
          </PageCard>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard sx={{ maxWidth: 1400, width: '100%' }}>
          {/* Header */}
          <Box sx={{ 
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #3b82f6',
            p: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AIIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}
                >
                  Create AI-Powered Appeal Letter
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    {company} | {center}
                  </Typography>
                </Stack>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBackToWorklist}
              sx={{ 
                borderColor: '#64748b',
                color: '#64748b',
                '&:hover': {
                  borderColor: '#475569',
                  backgroundColor: '#f8fafc'
                }
              }}
            >
              Back to Denials
            </Button>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label, index) => (
                <Step key={label} completed={index < activeStep}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step 1: Configure Appeal */}
            {activeStep === 0 && (
              <Stack spacing={3}>
                {/* Row 1: Procedure Category & Payer Template */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel>Procedure Category</InputLabel>
                      <Select
                        value={procedureCategory}
                        label="Procedure Category"
                        onChange={(e) => setProcedureCategory(e.target.value as ProcedureCategory)}
                      >
                        {PROCEDURE_CATEGORIES.map((cat) => (
                          <MenuItem key={cat.value} value={cat.value}>
                            <Typography>{cat.label}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel>Payer Template</InputLabel>
                      <Select
                        value={payerTemplateId}
                        label="Payer Template"
                        onChange={(e) => setPayerTemplateId(e.target.value as PayerTemplateId)}
                      >
                        {PAYER_TEMPLATES.map((template) => (
                          <MenuItem key={template.id} value={template.id}>
                            <Typography>{template.name}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Stack>

                {/* Row 2: Template Type & Appeal Level */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel>Template Type</InputLabel>
                      <Select
                        value={templateType}
                        label="Template Type"
                        onChange={(e) => setTemplateType(e.target.value as TemplateType)}
                      >
                        {TEMPLATE_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            <Box>
                              <Typography variant="body1">{type.label}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {type.description}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel>Appeal Level</InputLabel>
                      <Select
                        value={appealType}
                        label="Appeal Level"
                        onChange={(e) => setAppealType(e.target.value as AppealType)}
                      >
                        {APPEAL_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Stack>

                {/* Payer Address */}
                <TextField
                  fullWidth
                  label="Payer Appeal Address"
                  placeholder="Enter payer mailing address for appeal..."
                  value={payerAddress}
                  onChange={(e) => setPayerAddress(e.target.value)}
                  multiline
                  rows={2}
                  helperText="Enter the payer's appeal submission address"
                />

                {/* Payer template guidance */}
                {selectedPayerTemplate && (
                  <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f0f9ff', border: '1px solid #3b82f6' }}>
                    <Typography variant="subtitle2" sx={{ color: '#1e40af', mb: 1 }}>
                      {selectedPayerTemplate.name} - Strategy Guidance
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1e3a5f', mb: 1 }}>
                      {selectedPayerTemplate.toneGuidance}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {selectedPayerTemplate.specialConsiderations.slice(0, 3).map((consideration, idx) => (
                        <Chip 
                          key={idx} 
                          label={consideration} 
                          size="small" 
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Stack>
                  </Paper>
                )}

                {/* Additional Instructions */}
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Additional Instructions for AI"
                  placeholder="Enter any specific instructions or context you want the AI to consider when generating the appeal letter..."
                  value={additionalInstructions}
                  onChange={(e) => setAdditionalInstructions(e.target.value)}
                  helperText="Optional: Provide specific details or emphasis points"
                />
              </Stack>
            )}

            {/* Step 2: Clinical Documentation */}
            {activeStep === 1 && (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    Document clinical information to strengthen your appeal. The AI will use this data 
                    along with policy research to generate a compelling letter. 
                    {showPostOpCourse && (
                      <strong> Post-operative course is recommended for this denial type.</strong>
                    )}
                  </Typography>
                </Alert>
                
                <ClinicalSupplementsForm
                  value={clinicalSupplements}
                  onChange={setClinicalSupplements}
                  procedureCategory={procedureCategory}
                  denialCategory={denialCategory}
                  showPostOpCourse={showPostOpCourse}
                />
              </Box>
            )}

            {/* Step 3: AI Generation */}
            {activeStep === 2 && (
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
                {/* Left: Claim Context Summary */}
                <Box sx={{ flex: 1, maxWidth: { lg: 400 } }}>
                  <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', height: '100%' }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#1e293b' }}>
                      Claim Context
                    </Typography>
                    <ClaimContextPanel 
                      encounterId={encounterId}
                      accountId={accountId}
                    />
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#64748b' }}>
                      Policy Search
                    </Typography>
                    <PolicySearchPanel 
                      enabled={includePolicySearch}
                      onToggle={setIncludePolicySearch}
                      searchTerms={searchTermOverrides}
                      onSearchTermsChange={setSearchTermOverrides}
                    />
                  </Paper>
                </Box>
                
                {/* Right: Agent Chat */}
                <Box sx={{ flex: 2 }}>
                  <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#1e293b' }}>
                      <AIIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#3b82f6' }} />
                      AI Appeal Assistant
                    </Typography>
                    
                    {isLoading ? (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <CircularProgress size={60} sx={{ mb: 2 }} />
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          Generating Appeal Letter...
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          AI is analyzing claim context, searching policies, and drafting your letter
                        </Typography>
                      </Box>
                    ) : error ? (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        Failed to generate appeal letter. Please try again or contact support.
                      </Alert>
                    ) : (
                      <AgentChat
                        remitId={remitId}
                        claimIndex={claimIndex}
                        onAppealGenerated={handleAppealGenerated}
                        height="500px"
                      />
                    )}
                  </Paper>
                </Box>
              </Stack>
            )}

            {/* Step 4: Review & Finalize */}
            {activeStep === 3 && (
              <Stack spacing={3}>
                {generatedLetterId ? (
                  <>
                    <Alert severity="success" icon={<CheckIcon />}>
                      <Typography variant="body2">
                        <strong>Appeal letter generated successfully!</strong> Review the attachment checklist 
                        and click "Open Editor" to review and finalize the letter.
                      </Typography>
                    </Alert>
                    
                    {/* Summary of what was generated */}
                    <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f0fdf4', border: '1px solid #22c55e' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Appeal Letter Summary
                      </Typography>
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        <Chip label={`Template: ${TEMPLATE_TYPES.find(t => t.value === templateType)?.label}`} />
                        <Chip label={`Level: ${APPEAL_TYPES.find(t => t.value === appealType)?.label}`} />
                        <Chip label={`Payer: ${selectedPayerTemplate?.name}`} />
                        <Chip label={`Procedure: ${getProcedureCategoryLabel(procedureCategory)}`} />
                        <Chip label={`Policy Search: ${includePolicySearch ? 'Included' : 'Not included'}`} />
                      </Stack>
                    </Paper>
                    
                    {/* Attachment Checklist */}
                    <AttachmentChecklist
                      value={attachments}
                      onChange={setAttachments}
                      procedureCategory={procedureCategory}
                      denialCategory={denialCategory}
                    />
                  </>
                ) : (
                  <Alert severity="warning">
                    No letter has been generated yet. Please go back to Step 3 to generate the appeal.
                  </Alert>
                )}
              </Stack>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ color: '#64748b' }}
              >
                Back
              </Button>
              <Stack direction="row" spacing={2}>
                {activeStep === 2 && !isLoading && !generatedLetterId && (
                  <Button
                    variant="contained"
                    onClick={handleGenerateAppeal}
                    startIcon={<AIIcon />}
                    sx={{
                      backgroundColor: '#3b82f6',
                      '&:hover': { backgroundColor: '#2563eb' }
                    }}
                  >
                    Generate Appeal Letter
                  </Button>
                )}
                {activeStep === 3 && generatedLetterId && (
                  <Button
                    variant="contained"
                    onClick={() => navigate(`/appeals/edit/${generatedLetterId}?company=${company}&center=${center}`)}
                    startIcon={<EditIcon />}
                    sx={{
                      backgroundColor: '#3b82f6',
                      '&:hover': { backgroundColor: '#2563eb' }
                    }}
                  >
                    Open Letter Editor
                  </Button>
                )}
                {activeStep < 2 && (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{
                      backgroundColor: '#3b82f6',
                      '&:hover': { backgroundColor: '#2563eb' }
                    }}
                  >
                    Next
                  </Button>
                )}
              </Stack>
            </Box>
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default AppealCreate;
